import { useEffect, useState } from 'react';
import { type MetaFunction, type LoaderFunctionArgs } from '@remix-run/cloudflare';
import '~/styles/onboarding.css';
import { json, redirect } from '@remix-run/cloudflare';
import { useLoaderData } from '@remix-run/react';
import OtpVerification from '~/components/ui/OtpVerification';
import LoginForm from '~/components/ui/LoginForm';
import OnboardingFooter from '~/components/ui/OnboardingFooter';
import GreenPill from '~/components/ui/GreenPill';
import { userStore } from '~/lib/stores/user';
import { callLogoutAPI } from '~/components/header/Header.client';
import { getCurrencyFromCountry, type SupportedCurrency } from '~/utils/currency';
import IconsMapping from '@/components/ui/Icons/IconsMapping';
import HeroCarousel from '~/components/ui/HeroCarousel';
import { useEmailOtpFlow } from '~/hooks/useEmailOtpFlow';
import { isValidInviteCodeFormat, normalizeInviteCode } from '~/services/inviteCodeService.server';
import { clearReferralCookie, serializeReferralCookie } from '~/services/referralCookie.server';

// import FeatureShowcaseNew from '~/components/ui/FeatureShowCaseNew';
// import { faqData } from '~/utils/faqData';
// import PricingSection from '~/components/ui/PricingSection';

export const loader = async ({ request, context }: LoaderFunctionArgs) => {
  // Try to get country from Cloudflare headers
  let detectedCurrency: SupportedCurrency = 'USD';

  try {
    // Cloudflare provides the country code in the CF-IPCountry header
    const countryCode = request.headers.get('x-vercel-ip-country') || request.headers.get('CF-IPCountry') || 'US';

    if (countryCode && countryCode !== 'XX') {
      detectedCurrency = getCurrencyFromCountry(countryCode);
    }
  } catch (error) {
    console.error('Error detecting currency:', error);
  }

  const url = new URL(request.url);

  if (!url.searchParams.has('referral')) {
    return json({ currency: detectedCurrency });
  }

  const normalizedCode = normalizeInviteCode(url.searchParams.get('referral') ?? '');
  const referralCookie = isValidInviteCodeFormat(normalizedCode)
    ? await serializeReferralCookie(context.cloudflare.env, url, normalizedCode)
    : await clearReferralCookie(url);

  // Keep referral codes out of the address bar/history after the signed cookie
  // is captured. Other entry parameters (for example `prompt`) are preserved.
  url.searchParams.delete('referral');

  return redirect(`${url.pathname}${url.search}`, {
    headers: { 'Set-Cookie': referralCookie },
  });
};

export const meta: MetaFunction = () => {
  return [
    { title: 'Idea to App in minutes with Dualite' },

    // Preload critical hero image for faster LCP
    // {
    //   tagName: 'link',
    //   rel: 'preload',
    //   href: '/images/LoginBGnew.webp',
    //   as: 'image',
    //   fetchpriority: 'high',
    // },
  ];
};

const COLUMN_1_VIDEOS = [
  '/videos/onboarding/1Reserve_red.webm',
  '/videos/onboarding/dashboard_red.webm',
  '/videos/onboarding/Soothemi_red.webm',
  '/videos/onboarding/JaneAI_red.webm',
  '/videos/onboarding/NORDEN_red.webm',
];

const COLUMN_2_VIDEOS = [
  '/videos/onboarding/Horai_red.webm',
  '/videos/onboarding/YellowStudio_red.webm',
  '/videos/onboarding/sneaker_red.webm',
  '/videos/onboarding/memories_red.webm',
  '/videos/onboarding/Investify.webp',
];

export default function Onboarding() {
  const {
    email,
    setEmail,
    view,
    primaryCtaLabel,
    isPrimaryBusy,
    submitEmail,
    isSendingOtp,
    emailError,
    otp,
    setOtp,
    otpState,
    otpError,
    verifyOtp,
    resendOtp,
    canResend,
    resendCountdown,
    backToLanding,
  } = useEmailOtpFlow();

  console.log('onboarding');

  // FAQ accordion state
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const logOutSession = async () => {
    await callLogoutAPI();
  };

  useEffect(() => {
    localStorage.clear();

    // Clear user data from store
    console.log('[Header] Clearing user data from store');
    userStore.set({
      email: '',
      isSubscribed: false,
      maxPrompts: '0',
      promptsUsed: '0',
      planName: 'Free',
      name: '',
      inviteCode: '',
      topUpPrompts: '0',
      topUpPromptsUsed: '0',
      created_at: new Date(),
      image: '',
    });

    // logOutSession();

    const urlPrompt = new URLSearchParams(window.location.search).get('prompt');

    if (urlPrompt) {
      localStorage.setItem('prompt', urlPrompt);
    }
  }, []);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-[#FEFFFC] flex flex-col overflow-x-hidden">
      {/* 
        Full-Screen Split Layout Container 
        Mobile: Vertical Stack (Carousel Top -> Content Bottom)
        Desktop: Split Screen (Carousel Left -> Content Right)
      */}
      <div className="w-full min-h-screen flex flex-col min-[1200px]:flex-row relative overflow-x-hidden">
        {/* 
          1. Carousel Slot 
          Mobile: Fixed Height (45vh)
          Desktop: 50% Width, Sticky/Full Height
        */}
        {/* <div className="w-full h-[60vh] xs:h-[60vh] lg:h-screen min-[1200px]:w-1/2 xl:h-screen xl:sticky xl:top-0 relative z-0 p-3">
          <HeroCarousel />
        </div> */}

        {/* 
          2. Content Slot (Hero Text + Login)
          Mobile: Flows naturally below carousel
          Desktop: 50% Width, Full Height, Centered
        */}
        <div className="w-full flex-1 onboarding-bg flex flex-col items-center justify-center px-6 py-8 xl:w-1/2 xl:h-full xl:min-h-screen xl:px-12 relative z-10">
          <img
            src="/images/manOnHorse.webp"
            alt="man on horse"
            className="absolute bottom-0 left-0 w-[26%] max-w-[600px] h-auto pointer-events-none z-0"
          />

          {/* Main Content Section */}
          <div className="flex flex-col items-center justify-center w-full h-full min-h-[500px] max-w-[400px]">
            <div
              className="absolute z-0 pointer-events-none hidden min-[1200px]:block"
              style={{
                width: '54.75vw',
                height: '45.14vw',
                top: '-22.92vw',
                left: '6.67vw',
                transform: 'rotate(-45deg)',
                opacity: 0.25,
                background: 'linear-gradient(180deg, #FFFFFF 0%, rgba(159, 154, 147, 0.1) 100%)',
                filter: 'blur(100px)',
                WebkitMaskImage: 'linear-gradient(to left, black 0%, transparent 100%)',
                maskImage: 'linear-gradient(to left, black 0%, transparent 100%)',
              }}
            />
            {view === 'landing' ? (
              <div className="flex flex-col gap-10 items-center justify-center w-full max-w-lg relative z-10">
                {/* Hero Content */}

                <div id="onboarding-hero-content" className="flex flex-col gap-3 sm:gap-4 items-center w-full sm:w-fit">
                  <img src="/images/logo-black.svg" alt="Dualite" className="w-12 sm:w-10 mb-3" loading="eager" />

                  {/* Main Heading */}
                  <h1
                    className="text-3xl lg:text-[40px] font-normal leading-[1.2] tracking-[0] text-center"
                    style={{
                      color: '#1D1D1D',
                      fontFamily: 'Perfectly Nineties',
                    }}
                  >
                    Build websites and apps in minutes
                  </h1>

                  {/* Subtitle */}
                  <p
                    style={{
                      color: '#4D5763',
                      fontFamily: 'Neue Montreal',
                    }}
                    className="text-sm sm:text-base lg:text-md font-normal leading-[1.2]"
                  >
                    Trusted by 100k+ users in 150+ countries{' '}
                  </p>
                </div>

                {/* Login Form */}
                <LoginForm
                  email={email}
                  onEmailChange={setEmail}
                  onSubmit={submitEmail}
                  isBusy={isPrimaryBusy}
                  ctaLabel={primaryCtaLabel}
                  error={emailError}
                />

                {/* Scroll Down Indicator */}
                {/* <div className="scroll-indicator flex flex-col items-center gap-2 mt-16">
                <p className="text-[rgba(253,253,253,0.6)] text-sm sm:text-base font-normal tracking-wide">
                  Scroll to know more
                </p>
                <div className="scroll-arrow">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-[rgba(253,253,253,0.6)]"
                  >
                    <path
                      d="M12 5V19M12 19L19 12M12 19L5 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div> */}
              </div>
            ) : isSendingOtp ? (
              <div className="w-full max-w-md mx-auto font-inter-display px-4 flex flex-col items-center justify-center">
                <div className="flex flex-col gap-4 sm:gap-4">
                  <div className="flex flex-col gap-1">
                    <h1 className="text-[#FDFDFD] text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-medium leading-[1.2em] mb-2">
                      Verify your email
                    </h1>
                    <div className="text-[#696A71] text-base sm:text-md lg:text-md font-normal leading-[1.2em]">
                      <p>Sending verification code to your email...</p>
                    </div>
                  </div>
                  <div className="flex justify-center py-8">
                    <div className="w-8 h-8 border-4 border-[#696A71] border-t-[#F7F7F7] rounded-full animate-spin"></div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full max-w-md mx-auto flex flex-col items-center justify-center">
                <OtpVerification
                  email={email}
                  otp={otp}
                  onOtpChange={setOtp}
                  state={otpState}
                  error={otpError}
                  canResend={canResend}
                  resendCountdown={resendCountdown}
                  onVerify={verifyOtp}
                  onResend={resendOtp}
                  onBack={backToLanding}
                />
              </div>
            )}
          </div>
        </div>

        {/* Right Section: Showcase Grid */}
        <div className="hidden min-[1200px]:flex w-[30%] xl:w-[32%] bg-[#FFFFFF] py-6 px-4 flex-row gap-4 overflow-hidden h-screen sticky top-0">
          {/* Column 1 */}
          <div className="flex-1 flex flex-col gap-4 h-full">
            {COLUMN_1_VIDEOS.map((src, idx) => {
              const weights = ['flex-[1.5]', 'flex-[1]', 'flex-[2]', 'flex-[1.5]', 'flex-[1.2]'];
              return src.includes('.webp') ? (
                <ShowcaseImage key={`col1-${idx}`} src={src} className={weights[idx]} />
              ) : (
                <ShowcaseVideo key={`col1-${idx}`} src={src} className={weights[idx]} />
              );
            })}
          </div>
          {/* Column 2 */}
          <div className="flex-1 flex flex-col gap-4 h-full">
            {COLUMN_2_VIDEOS.map((src, idx) => {
              const weights = ['flex-[1]', 'flex-[1.8]', 'flex-[1.2]', 'flex-[2]', 'flex-[1]'];
              return src.includes('.webp') ? (
                <ShowcaseImage key={`col2-${idx}`} src={src} className={weights[idx]} />
              ) : (
                <ShowcaseVideo key={`col2-${idx}`} src={src} className={weights[idx]} />
              );
            })}
          </div>
        </div>
      </div>

      {/* Feature Showcase Section */}
      {/* <FeatureShowcase /> */}
      {/* <FeatureShowcaseNew /> */}

      {/* Pricing Section */}
      {/* <PricingSection
        currency={currency}
        onScrollToTop={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        onContactSales={() => window.open('https://calendly.com/singhvirohan/dualite-feedback', '_blank')}
      /> */}

      {/* FAQ Section */}
      {/* <div id="faqs" className="w-full bg-[#10110C] px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24 scroll-mt-4">
        <div className="max-w-3xl mx-auto">
          <h2
            className="text-white text-3xl sm:text-4xl lg:text-5xl font-semibold text-center mb-12 lg:mb-16 group relative block mx-auto"
            style={{ fontFamily: 'Inter Display' }}
          >
            Your questions answered
          </h2>

          <div className="space-y-0">
            {faqData.map((faq, index) => (
              <div key={index} className="border-b border-white/10">
                <button
                  type="button"
                  onClick={() => toggleFaq(index)}
                  aria-expanded={openFaqIndex === index}
                  aria-controls={`faq-panel-${index}`}
                  className="w-full flex items-center justify-between py-6 text-left group hover:opacity-80 transition-opacity"
                >
                  <span
                    className="text-white text-base sm:text-lg lg:text-xl font-normal pr-4"
                    style={{ fontFamily: 'Inter Display' }}
                  >
                    {faq.question}
                  </span>
                  <svg
                    className={`w-5 h-5 text-white/70 shrink-0 transition-transform duration-300 ${
                      openFaqIndex === index ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div
                  id={`faq-panel-${index}`}
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openFaqIndex === index ? 'max-h-96 pb-6' : 'max-h-0'
                  }`}
                >
                  <p
                    className="text-white/70 text-sm sm:text-base lg:text-lg leading-relaxed"
                    style={{ fontFamily: 'Inter Display' }}
                  >
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-white/60 text-center text-sm sm:text-base mt-12" style={{ fontFamily: 'Inter Display' }}>
            Can't find what you're looking for?{' '}
            <span className="block sm:inline py-2">
              Contact our{' '}
              <a href="mailto:support@dualite.dev" className="text-white font-medium hover:underline transition-all">
                customer support team
              </a>
            </span>
          </p>
        </div>
      </div> */}

      {/* Footer Section */}
      {/* <OnboardingFooter /> */}
    </div>
  );
}

function ShowcaseVideo({ src, className }: { src: string; className?: string }) {
  return (
    <div className={`w-full rounded-2xl overflow-hidden shadow-sm ${className}`}>
      <video src={src} autoPlay loop muted playsInline preload="metadata" className="w-full h-full object-cover" />
    </div>
  );
}

function ShowcaseImage({ src, className }: { src: string; className?: string }) {
  return (
    <div className={`w-full rounded-2xl overflow-hidden shadow-sm ${className}`}>
      <img src={src} alt="" className="w-full h-full object-cover" />
    </div>
  );
}
